import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Shepherd from "shepherd.js";
import api from "@/lib/api";

/**
 * Phase B4 — Shepherd.js tour engine.
 *
 * Why a hook (not a component): Shepherd creates DOM imperatively
 * (overlay, popover element, arrow), so wrapping it in JSX gives no
 * benefit. The hook owns one Tour instance for the lifetime of the
 * mount, syncs `current_step_id` to the backend on every step transition,
 * and persists `tour_status` on completion / skip.
 *
 * Inputs:
 *   - active: boolean — when true the hook ensures the tour is showing
 *     (resumes at `currentStepId` if mid-flight).
 *   - currentStepId / completedStepIds: hydrated from /api/onboarding/me.
 *   - onTourEnd: callback fired with the final status ("completed" |
 *     "skipped") so the parent can close its modal / refresh state.
 *   - seedSampleData: async callback called when the user ticks the
 *     "Seed sample data" box on the welcome step. Resolved before we
 *     advance the tour so the user sees the success toast before the
 *     anchor highlights move on.
 */
const STEP_DEFS = [
  {
    id: "welcome",
    title: "Welcome to EntryBook",
    body: "Let's get your first site live in under 5 minutes. You can skip anytime.",
    anchor: null,
    centred: true,
    showSampleToggle: true,
  },
  {
    id: "sidebar-sites",
    title: "Step 1 — Create your first site",
    body: "A site is a physical location with kiosks, hosts and visitors.",
    anchor: '[data-tour="nav-sites"]',
    primary: { label: "Take me to Sites", route: "/sites" },
    skipLabel: "I already have a site",
  },
  {
    id: "sidebar-users",
    title: "Step 2 — Invite your first host",
    body: "Hosts receive notifications when their visitors arrive.",
    anchor: '[data-tour="nav-users"]',
    primary: { label: "Take me to Users", route: "/users" },
    skipLabel: "Skip step",
  },
  {
    id: "sidebar-kiosks",
    title: "Step 3 — Pair a kiosk",
    body: "Open your tablet, type the pairing PIN — and you're live.",
    anchor: '[data-tour="nav-kiosks"]',
    primary: { label: "Take me to Kiosks", route: "/kiosks" },
    skipLabel: "Skip step",
  },
  {
    id: "sidebar-preregs",
    title: "Step 4 — Pre-register a visitor",
    body: "Test the flow end-to-end: the visitor scans a QR at the kiosk.",
    anchor: '[data-tour="nav-preregs"]',
    primary: { label: "Take me to Pre-registrations", route: "/pre-registrations" },
    skipLabel: "Skip step",
  },
  {
    id: "finish",
    title: "You're ready to go live",
    body: "You can restart this tour anytime from the Help menu. Your go-live checklist stays in the sidebar.",
    anchor: null,
    centred: true,
    primary: { label: "Finish" },
    isFinal: true,
  },
];

export const TOUR_STEP_IDS = STEP_DEFS.map((s) => s.id);

export default function useTourEngine({ active, currentStepId, completedStepIds, onTourEnd, seedSampleData }) {
  const tourRef = useRef(null);
  const navigate = useNavigate();
  // Latest props kept on a ref so the Shepherd handlers (registered once)
  // always see fresh values.
  const propsRef = useRef({ currentStepId, completedStepIds, onTourEnd, seedSampleData });
  propsRef.current = { currentStepId, completedStepIds, onTourEnd, seedSampleData };

  useEffect(() => {
    if (!active) {
      if (tourRef.current?.isActive?.()) tourRef.current.cancel();
      return;
    }
    if (tourRef.current?.isActive?.()) return;   // already showing.

    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      keyboardNavigation: false,    // We handle Esc ourselves via skip-link.
      defaultStepOptions: {
        cancelIcon: { enabled: false },
        scrollTo: { behavior: "smooth", block: "center" },
        modalOverlayOpeningPadding: 6,
        modalOverlayOpeningRadius: 6,
        classes: "eb-tour-step",
      },
    });

    // PATCH /api/onboarding/me — debounced via the Promise: we never block
    // the tour on the network round-trip, but we always send the update.
    const persistStep = (stepId, extra = {}) => {
      api.patch("/onboarding/me", {
        tour_status: "in_progress",
        current_step_id: stepId,
        completed_step_ids: extra.tickedIds || [],
      }).catch(() => { /* visual stays in sync; backend will heal next refresh */ });
    };

    STEP_DEFS.forEach((def, idx) => {
      const progressBadge = `<span class="shepherd-progress" data-testid="tour-progress-${def.id}">Step ${idx + 1} of ${STEP_DEFS.length}</span>`;
      const skipLink = `<button type="button" class="shepherd-button shepherd-button-skip" data-action="skip" data-testid="tour-skip-${def.id}">Skip tour</button>`;

      let bodyHtml = `<p>${def.body}</p>`;
      if (def.showSampleToggle) {
        bodyHtml += `
          <label class="eb-tour-sample-toggle" data-testid="tour-sample-toggle">
            <input type="checkbox" data-action="toggle-sample" />
            <span>Seed sample data for me to play with (1 site · 2 hosts · 1 pre-reg · 1 contractor).</span>
          </label>`;
      }

      // Build buttons. The final step has "Restart tour" + "Finish";
      // the welcome step has "Start tour" + "Skip"; intermediate steps
      // have "Take me to <X>" + "<skipLabel>".
      const buttons = [];
      if (def.id === "welcome") {
        buttons.push({
          text: "Start tour",
          classes: "shepherd-button",
          action() {
            const root = document.querySelector(`[data-testid="tour-sample-toggle"]`);
            const sampleEl = root?.querySelector('input[type="checkbox"]');
            const wantsSample = !!sampleEl?.checked;
            const advance = () => { persistStep(STEP_DEFS[idx + 1].id, { tickedIds: ["welcome"] }); tour.next(); };
            if (wantsSample && propsRef.current.seedSampleData) {
              propsRef.current.seedSampleData()
                .then(advance)
                .catch(advance);   // refuse-with-409 is fine, just continue.
            } else { advance(); }
          },
        });
      } else if (def.primary) {
        buttons.push({
          text: def.primary.label,
          classes: "shepherd-button",
          action() {
            const tickedIds = Array.from(new Set([...(propsRef.current.completedStepIds || []), def.id]));
            if (def.isFinal) {
              persistStep(null, { tickedIds });
              api.patch("/onboarding/me", { tour_status: "completed", completed_step_ids: tickedIds })
                .catch(() => {});
              tour.complete();
              return;
            }
            const next = STEP_DEFS[idx + 1];
            persistStep(next.id, { tickedIds });
            tour.next();
            if (def.primary.route) navigate(def.primary.route);
          },
        });
      }
      if (def.id === "welcome") {
        buttons.push({
          text: "Skip",
          classes: "shepherd-button shepherd-button-secondary",
          action() { tour.cancel(); },
        });
      } else if (def.skipLabel) {
        buttons.push({
          text: def.skipLabel,
          classes: "shepherd-button shepherd-button-secondary",
          action() {
            // Step-level skip — advance without routing. Tick the
            // current step as completed so it shows green if/when
            // the user comes back to it.
            const tickedIds = Array.from(new Set([...(propsRef.current.completedStepIds || []), def.id]));
            if (def.isFinal) { tour.complete(); return; }
            const next = STEP_DEFS[idx + 1];
            persistStep(next.id, { tickedIds });
            tour.next();
          },
        });
      }

      tour.addStep({
        id: def.id,
        title: `<div style="display:flex;align-items:center;gap:10px;width:100%;justify-content:space-between"><span>${def.title}</span>${progressBadge}</div>`,
        text: `${bodyHtml}<div style="margin-top:10px;text-align:right">${skipLink}</div>`,
        attachTo: def.anchor ? { element: def.anchor, on: "right" } : undefined,
        highlightClass: "shepherd-target-highlighted",
        modalOverlayOpeningPadding: def.centred ? 0 : 6,
        canClickTarget: false,
        when: {
          show() {
            // Hook the skip link (rendered as raw HTML inside the body).
            const popoverEl = this.getElement();
            const skipBtn = popoverEl?.querySelector('[data-action="skip"]');
            if (skipBtn) {
              skipBtn.addEventListener("click", (e) => {
                e.preventDefault();
                tour.cancel();
              });
            }
          },
        },
        buttons,
      });
    });

    tour.on("cancel", () => {
      api.patch("/onboarding/me", { tour_status: "skipped", current_step_id: null }).catch(() => {});
      propsRef.current.onTourEnd?.("skipped");
    });
    tour.on("complete", () => {
      api.patch("/onboarding/me", { tour_status: "completed", current_step_id: null,
        completed_step_ids: STEP_DEFS.map((s) => s.id) }).catch(() => {});
      propsRef.current.onTourEnd?.("completed");
    });

    tourRef.current = tour;
    // Resume at the saved step (defaults to "welcome" on a fresh tour).
    const resumeId = propsRef.current.currentStepId
      && STEP_DEFS.find((s) => s.id === propsRef.current.currentStepId)
        ? propsRef.current.currentStepId : "welcome";
    if (resumeId === "welcome") {
      // First entry — mark as in_progress in the background.
      api.patch("/onboarding/me", { tour_status: "in_progress", current_step_id: "welcome" })
        .catch(() => {});
    }
    tour.start();
    tour.show(resumeId);

    return () => {
      if (tourRef.current?.isActive?.()) tourRef.current.cancel();
      tourRef.current = null;
    };
    // We intentionally do not re-run the effect when currentStepId
    // changes — `propsRef` keeps it fresh, and re-creating the tour on
    // every step would cause Shepherd to flash.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);
}
